// How the Structure tab names things: sections (built-in names are ours, an Admin's custom type
// carries its own), pinned designs («Логотип слева · Светлый»), and the preferences summary
// (design: prefSum, palSw).
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isLayout, resolvePalette, sectionType, type Palette } from '@/design-system';
import ruLibrary from '@/i18n/ru/library.json';
import { usePresetLabels } from '@/features/library/labels';
import type { PinnedPreset, Preferences } from './api';

/** The API seeds the design's demo presets named after their layout in Russian (library/seed.py). */
function isDemoName(p: Pick<PinnedPreset, 'name' | 'layout' | 'mirrored'>): boolean {
  if (!isLayout(p.layout)) return false;
  const base = (ruLibrary.layouts as Record<string, string>)[p.layout];
  return p.name === (p.mirrored ? `${base} · зеркально` : base);
}

export function useStructureLabels() {
  const { t } = useTranslation('library');
  const { section } = usePresetLabels();

  /** A design's name in the UI's language (a demo preset is named after its layout). */
  const designName = useCallback(
    (p: Pick<PinnedPreset, 'name' | 'layout' | 'mirrored'>) => {
      if (!isDemoName(p) || !isLayout(p.layout)) return p.name;
      const base = t(`layouts.${p.layout}`);
      return p.mirrored ? t('mirrored', { name: base }) : base;
    },
    [t],
  );

  const modeName = useCallback((mode: string) => (['light', 'soft', 'inv', 'acc'].includes(mode) ? t(`modes.${mode as 'light'}`) : mode), [t]);

  /** «Логотип слева · Светлый» (design: prN). */
  const designLabel = useCallback((p: PinnedPreset) => `${designName(p)} · ${modeName(p.color_mode)}`, [designName, modeName]);

  return { section, designName, modeName, designLabel, sectionDef: sectionType };
}

/** The palette every thumbnail of the site paints with: «AI решит» shows the brand palette (design: PAL). */
export function useSitePalette(prefs: Preferences | undefined, brandColors: readonly string[] | undefined): Palette {
  const key = prefs?.palette ?? 'auto';
  return useMemo(() => (key === 'auto' ? resolvePalette('brand', brandColors) : resolvePalette(key, brandColors)), [key, brandColors]);
}

const UPPER = (x: string) => x.toUpperCase();

/**
 * The «Предпочтения для AI» summary: the chosen palette, font, tone, languages and slogan, then «остальное
 * решит AI»; «AI решает всё сам» when nothing is chosen. `swatches` are the palette's background, ink,
 * accent and placeholder (design: palSw), empty on auto.
 */
export function usePrefSummary(prefs: Preferences | undefined, brandName: string, palette: Palette) {
  const { t } = useTranslation('structure');
  const { t: tl } = useTranslation('library');
  return useMemo(() => {
    const p = prefs ?? {};
    const pal = p.palette ?? 'auto';
    const parts = [
      pal !== 'auto' ? (pal === 'brand' ? tl('palettes.brand', { name: brandName }) : tl(`palettes.${pal}`)) : null,
      p.font && p.font !== 'auto' ? t(`fonts.${p.font}`) : null,
      p.tone && p.tone !== 'auto' ? t('prefs.tone_part', { tone: t(`tones.${p.tone}`) }) : null,
      p.languages?.length ? p.languages.map(UPPER).join('/') : null,
      p.slogan ? `«${p.slogan.slice(0, 24)}${p.slogan.length > 24 ? '…' : ''}»` : null,
    ].filter(Boolean);
    const summary = parts.length ? t('prefs.summary', { parts: parts.join(' · ') }) : t('prefs.auto');
    const swatches = pal === 'auto' ? [] : [palette.bg, palette.ink, palette.acc, palette.ph];
    return { summary, swatches, auto: pal === 'auto' };
  }, [prefs, brandName, palette, t, tl]);
}
